// La première ligne d'un historique, écrite à partir de ce que le client vient
// de répondre.
//
// L'historique d'adresses du 5669 commence par l'adresse actuelle, et les
// antécédents personnels par l'occupation actuelle : le client venait de les
// saisir quelques écrans plus tôt et devait les retaper. On pré-remplit la
// PREMIÈRE ligne seulement, et seulement quand la liste est encore vide. Rien
// n'est verrouillé : le client corrige ou efface comme n'importe quelle ligne,
// et les dates restent à lui (nous ne savons pas depuis quand il y habite).
//
// Volontairement côté questionnaire : passer d'une réponse (objet adresse,
// code de statut) aux colonnes d'un tableau demande une transformation, pas une
// correspondance de noms comme en base.

import { COUNTRIES } from "@/data/questions";

type Reponses = Record<string, unknown>;

/** Le nom du pays, à partir de son code ISO (« CA » → « Canada »). */
const nomPays = (code: string): string =>
  COUNTRIES.find((c) => c.code === code)?.name ?? code;

const texte = (v: unknown): string => (typeof v === "string" ? v.trim() : "");

const objet = (v: unknown): Record<string, unknown> =>
  v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : {};

/** L'adresse de résidence si elle existe, l'adresse postale sinon. */
const adresseActuelle = (a: Reponses): Record<string, unknown> => {
  const residence = objet(a.residenceAddress);
  return Object.keys(residence).length ? residence : objet(a.postalAddress);
};

const villePays = (a: Reponses): string => {
  const adr = adresseActuelle(a);
  const pays = texte(adr.country) || texte(a.currentResidenceCountry);
  return [texte(adr.city), pays && nomPays(pays)].filter(Boolean).join(", ");
};

/** Colonnes pré-remplies par question, vides retirées. */
const MODELES: Record<string, (a: Reponses) => Record<string, string>> = {
  // IMM 5669, historique d'adresses des 10 dernières années.
  sponsoredAddressHistory: (a) => {
    const adr = adresseActuelle(a);
    return {
      street: texte(adr.street),
      city: texte(adr.city),
      provinceState: texte(adr.province) || texte(adr.provinceState),
      postalCode: texte(adr.postalCode),
      country: texte(adr.country) || texte(a.currentResidenceCountry),
    };
  },
  // IMM 5669, antécédents personnels des 10 dernières années.
  // Le statut dans le pays n'est pas repris : la question le stocke sous forme
  // de code (« travailleur ») et la colonne attend du texte libre.
  sponsoredActivityHistory: (a) => ({
    activity: texte(a.sponsoredCurrentOccupation),
    cityCountry: villePays(a),
  }),
};

/**
 * Ce qu'il faut écrire dans la première ligne de `externalId`, ou null.
 * `colonnes` limite le résultat aux colonnes que la question déclare vraiment.
 */
export function premiereLignePreRemplie(
  externalId: string,
  reponses: Reponses,
  colonnes: Array<{ key: string }>,
): Record<string, string> | null {
  const modele = MODELES[externalId];
  if (!modele) return null;
  const connues = new Set(colonnes.map((c) => c.key));
  const ligne: Record<string, string> = {};
  for (const [k, v] of Object.entries(modele(reponses))) {
    if (v && connues.has(k)) ligne[k] = v;
  }
  return Object.keys(ligne).length ? ligne : null;
}
